import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import FormPageLayout from "../components/form-page-layout";
import { WORK_API_URL } from "../config/enviroments";
import { InterviewDetails } from "./types";

const InterviewSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const formId = query.get("formId");

  const [interviewDetails, setInterviewDetails] =
    useState<InterviewDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applicationFormIdNumber, setApplicationFormIdNumber] = useState<
    number | null
  >(null);

  useEffect(() => {
    // Always fetch fresh data from server if we have formId
    if (formId) {
      setLoading(true);
      axios
        .get(
          `${WORK_API_URL}/hr-api/v1/interview-shift-entries/${formId}/interview-details`
        )
        .then((res) => {
          if (res.data.success && res.data.interviewDetails) {
            setInterviewDetails(res.data.interviewDetails);
          } else {
            // Khi không tìm thấy interview details, vẫn có applicationFormId
            if (res.data.applicationFormId) {
              setApplicationFormIdNumber(res.data.applicationFormId);
            }
            toast.error("Không tìm thấy thông tin phỏng vấn");
            // navigate("/");
          }
        })
        .catch((err) => {
          setError(
            `Không thể tải thông tin phỏng vấn. ${err.response.data.message || err.message}`
          );
          // navigate("/");
        })
        .finally(() => {
          setLoading(false);
        });
    }

    // If no formId, redirect to home
    if (!formId) {
      navigate("/");
    }
  }, [formId, navigate]);

  if (loading) {
    return (
      <FormPageLayout
        title="Thông tin phỏng vấn"
        formId={formId || ""}
        applicationFormIdNumber={applicationFormIdNumber}
      >
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 400 }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </FormPageLayout>
    );
  }

  if (error) {
    return (
      <FormPageLayout
        title="Thông tin phỏng vấn"
        formId={formId || ""}
        applicationFormIdNumber={applicationFormIdNumber}
      >
        <div className="alert alert-danger" role="alert">
          <strong>Lỗi:</strong> {error}
        </div>
        <div className="text-center mt-3">
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/interview/list?formId=${formId}`)}
          >
            Xem lịch đăng ký
          </button>
        </div>
      </FormPageLayout>
    );
  }

  if (!interviewDetails) {
    return (
      <FormPageLayout
        title="Thông tin phỏng vấn"
        formId={formId || ""}
        applicationFormIdNumber={applicationFormIdNumber}
      >
        <div className="alert alert-warning" role="alert">
          <strong>Thông báo:</strong> Không tìm thấy thông tin phỏng vấn
        </div>
        <div className="text-center mt-3">
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/interview/list?formId=${formId}`)}
          >
            Xem lịch đăng ký
          </button>
        </div>
      </FormPageLayout>
    );
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert("Đã sao chép link!");
  };

  return (
    <FormPageLayout
      title="Thông tin phỏng vấn"
      formId={formId || ""}
      applicationFormIdNumber={
        interviewDetails.applicationFormId
          ? Number(interviewDetails.applicationFormId)
          : null
      }
      description="Đăng ký phỏng vấn thành công!"
    >
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card shadow">
            <div className="card-body text-center p-5">
              <div className="mb-4">
                <i
                  className="bi bi-check-circle-fill text-success"
                  style={{ fontSize: "4rem" }}
                ></i>
              </div>

              <div className="alert alert-info text-start">
                <h5 className="alert-heading">Thông tin phỏng vấn của bạn:</h5>
                <p className="mb-2 text-muted">
                  <small>
                    Mã hồ sơ: {interviewDetails.applicationFormId || formId}
                  </small>
                </p>
                <hr />
                <p className="mb-2">
                  <strong>Ngày phỏng vấn:</strong>{" "}
                  {dayjs(interviewDetails.date).format("DD/MM/YYYY")}
                </p>
                <p className="mb-2">
                  <strong>Giờ phỏng vấn:</strong> {interviewDetails.startTime} -{" "}
                  {interviewDetails.endTime}
                </p>
                <p className="mb-2">
                  <strong>Địa điểm:</strong>{" "}
                  {interviewDetails.location || "Sẽ thông báo sau qua email"}
                </p>
                <div className="mt-3">
                  <strong>Link phỏng vấn online:</strong>
                  {interviewDetails.googleMeetLink ? (
                    <div className="input-group mt-2">
                      <input
                        type="text"
                        className="form-control"
                        value={interviewDetails.googleMeetLink}
                        readOnly
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => {
                          if (interviewDetails.googleMeetLink) {
                            copyToClipboard(interviewDetails.googleMeetLink);
                          }
                        }}
                      >
                        <i className="bi bi-clipboard"></i> Sao chép
                      </button>
                    </div>
                  ) : (
                    <p className="text-muted mt-2">
                      Có lỗi khi lấy link Google Meet. Vui lòng liên hệ lại HR.
                    </p>
                  )}
                </div>
              </div>

              <div className="alert alert-warning text-start mt-4">
                <h6 className="alert-heading">Lưu ý:</h6>
                <ul className="mb-0">
                  <li>
                    Thông tin đăng ký phỏng vấn đã được gửi qua email ứng tuyển
                    của bạn
                  </li>
                  <li>
                    Vui lòng tham gia link online trước 5 phút để ổn định đường
                    truyền
                  </li>
                  <li>Kiểm tra email để nhận thông tin chi tiết</li>
                  <li>
                    Nếu cần hỗ trợ, vui lòng liên hệ bộ phận HR qua email:
                    tuyendung@4-handy.com
                  </li>
                </ul>
              </div>

              <div className="mt-4">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={() => navigate(`/?formId=${formId}`)}
                >
                  Về trang chủ
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FormPageLayout>
  );
};

export default InterviewSuccessPage;
